import React, { useEffect, useRef, useState } from "react";
import { UserData } from "./UserData";
import ListView from "../../component/ListView/ListView";
import { showToast } from "../../utils/Toastify/Toastify";
import { userDelete } from "../../api/HttpApis";
import { useLocation, useNavigate } from "react-router-dom";
import UserInvite from "../userInvite/UserInvite";
import { useSearch } from "../../context/SearchContext";
import { useRbac } from "../../rbac/RbacContext";
import { FiAlertTriangle } from "react-icons/fi";
import SendEmailModal from "./SendEmailModal";
import style from "./Users.module.css";

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

// Filter dropdown options for the Users list.
const USER_TYPE_OPTIONS = [
  { label: "Premium", value: "Premium" },
  { label: "Standard", value: "Standard" },
];
// Matches the exact, case-sensitive Subscription.platform values the backend
// filter compares against (see SubscribersController.list()) — options here
// that don't exist in the data (old "Apple"/"Manual") never matched anything.
const PLATFORM_OPTIONS = [
  { label: "Stripe", value: "stripe" },
  { label: "Web", value: "web" },
  { label: "Google Play", value: "google_play" },
  { label: "Roku", value: "roku" },
  { label: "Fire TV", value: "fire_tv" },
  { label: "Amazon", value: "amazon" },
  { label: "Free", value: "free" },
  { label: "Admin-granted", value: "admin" },
];
// Labelled by what the filter actually measures — whether the address was
// confirmed — not by signup progress: legacy, admin-created and bulk-imported
// accounts are never sent a code, so they read as "Email not verified" despite
// being complete signups. That is also why this defaults to "All" (no value)
// rather than hiding unverified accounts, which would hide those too.
const SIGNUP_STATE_OPTIONS = [
  { label: "Email verified", value: "verified" },
  { label: "Email not verified", value: "pending" },
];
const DATE_OPTIONS = [
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
  { label: "Last year", value: "year" },
];

const Users = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { searchQuery } = useSearch();
  const { can } = useRbac();
  const canCreateUser = can("users", "create");
  const canEmail = can("users", "email");
  const [user, setUser] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [handleEditId, setHandleEditId] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  // Starts true so the first paint shows the loader, not "No data found".
  const [loading, setLoading] = useState(true);
  // The list is fetched from two places (search/filter debounce and the
  // pager), so only the latest request may set rows or clear the loader.
  const latestRequest = useRef(0);
  const [filters, setFilters] = useState({
    user_type: "",
    platform: "",
    date: "",
    signup_state: "",
  });
  // "Send email" selection: explicit ids (kept across pages), or every user
  // matching the current search + filters (`allMatching`), resolved server-side.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [emailAudience, setEmailAudience] = useState(null);
  // Row checkboxes only show once the admin starts a "Send email" selection.
  const [selecting, setSelecting] = useState(false);
  // Header search, debounced so each keystroke doesn't refetch. ListView does
  // the fetching itself when `queryKey` changes (see below) — the page must not
  // also fetch, or every load would request the list twice.
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchData = async (page, limit) => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const { data, totalCount } = await UserData(
        page,
        limit,
        debouncedSearch,
        filters
      );
      if (requestId !== latestRequest.current) return;
      setUser(data || []);
      setTotalCount(totalCount);
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      const response = error?.response?.data;
      if (response?.errors) {
        const fieldErrors = Object.values(response.errors).flat();
        if (fieldErrors.length > 0) {
          showToast(fieldErrors[0], "error");
        } else if (response.message) {
          showToast(response.message, "error");
        }
      } else if (response?.message) {
        showToast(response.message, "error");
      }
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.openModal) {
      setIsModalOpen(true);
      setHandleEditId(null);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // A changed search or filter changes WHICH users are listed, so any existing
  // selection (and "all matching") no longer describes what the admin sees.
  useEffect(() => {
    setSelectedIds(new Set());
    setAllMatching(false);
  }, [debouncedSearch, filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

 const handleView = (id) => {
  navigate(`/mainlayout/users/${id}`);
};

  const handleEdit = (id) => {
    setEditingId(id);
    setTimeout(() => {
      setHandleEditId(id);
      setIsModalOpen(true);
      setEditingId(null);
    }, 500);
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this user?"
    );
    if (!confirmed) return;

    setDeletingId(id);
    try {
      const response = await userDelete(id);
      setUser((prev) => prev.filter((item) => item.id !== id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      showToast(response.data.message, "success");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to delete user.",
        "error"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const pageIds = user.map((item) => item.id);
  const isSelected = (id) => allMatching || selectedIds.has(id);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => isSelected(id));
  const selectedCount = allMatching ? totalCount : selectedIds.size;

  const toggleSelect = (id) => {
    if (allMatching) {
      // Unticking one row out of "all matching" falls back to this page minus it.
      setAllMatching(false);
      setSelectedIds(new Set(pageIds.filter((x) => x !== id)));
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePage = () => {
    if (allPageSelected) {
      setAllMatching(false);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => new Set([...prev, ...pageIds]));
    }
  };

  const clearSelection = () => {
    setAllMatching(false);
    setSelectedIds(new Set());
  };

  const stopSelecting = () => {
    clearSelection();
    setSelecting(false);
  };

  const openEmail = () => {
    setEmailAudience(
      allMatching
        ? {
            all_matching: true,
            filters: { ...filters, search: debouncedSearch || "" },
          }
        : { user_ids: [...selectedIds] },
    );
  };

  const cardFor = (item) => ({
    id: item.id,
    ...(canEmail && selecting && {
      selected: isSelected(item.id),
      onSelect: () => toggleSelect(item.id),
    }),
    thumbLabel: initials(item.name),
    title: item.name,
    meta: [item.email, item.updatedDate || "—"].filter(Boolean).join(" · "),
    badge: item.user ? { label: item.user, tone: "neutral" } : null,
    badges: [
      item.emailVerified === false
        ? { label: "Email not verified", tone: "warning" }
        : null,
    ],
    // Clicking the row opens the detail page, same as the eye/View action.
    // (The action buttons stopPropagation, so they won't double-trigger this.)
    onClick: () => handleView(item.id),
    actions: [
      {
        icon: "view",
        label: "View",
        onClick: () => handleView(item.id),
      },
      {
        icon: "edit",
        label: "Edit",
        disabled: editingId === item.id,
        onClick: () => handleEdit(item.id),
      },
      {
        icon: "delete",
        label: "Delete",
        disabled: deletingId === item.id,
        onClick: () => handleDelete(item.id),
      },
    ],
  });

  const stats = [
    { label: "Total Users", value: (totalCount || 0).toLocaleString() },
  ];

  // Bulk selections spell out how the send works: mail is queued and sent in
  // the background. The backend skips unsubscribed users, and — for "all
  // matching" only — unverified ones; hand-picked users get it either way.
  const bulkWarning = allMatching
    ? "Emails are added to a queue and sent in the background, so they may take a few minutes to arrive. Users whose email is not verified, or who have unsubscribed, will not receive this email."
    : allPageSelected
      ? "Emails are added to a queue and sent in the background, so they may take a few minutes to arrive. Users who have unsubscribed will not receive this email."
      : "";

  const emailActions = canEmail && pageIds.length > 0 && (
    selecting ? (
      <>
        <button type="button" className={style.cancelBtn} onClick={stopSelecting}>
          Cancel
        </button>
        <button
          type="button"
          className={style.sendBtn}
          disabled={selectedCount === 0}
          onClick={openEmail}
        >
          {selectedCount > 0
            ? `Send to ${selectedCount.toLocaleString()}`
            : "Select users"}
        </button>
      </>
    ) : (
      <button
        type="button"
        className={style.sendBtn}
        onClick={() => setSelecting(true)}
      >
        Send email
      </button>
    )
  );

  const selectionBar = canEmail && selecting && pageIds.length > 0 && (
    <div className={style.selectionWrap}>
      <div className={style.selectionBar}>
        <label className={style.selectPage}>
          <input
            type="checkbox"
            checked={allPageSelected}
            onChange={togglePage}
          />
          Select page
        </label>
        {selectedCount > 0 && (
          <span className={style.selectionCount}>
            {allMatching
              ? `All ${totalCount.toLocaleString()} matching users selected`
              : `${selectedCount.toLocaleString()} selected`}
          </span>
        )}
        {!allMatching && allPageSelected && totalCount > selectedIds.size && (
          <button
            type="button"
            className={style.linkBtn}
            onClick={() => setAllMatching(true)}
          >
            Select all {totalCount.toLocaleString()} matching users
          </button>
        )}
        {selectedCount > 0 && (
          <button
            type="button"
            className={style.linkBtn}
            onClick={clearSelection}
          >
            Clear
          </button>
        )}
      </div>
      {bulkWarning && (
        <div className={style.bulkWarning} role="alert">
          <FiAlertTriangle size={16} className={style.bulkWarningIcon} />
          <span>{bulkWarning}</span>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <ListView
        heading="Users"
        rowsOnly
        data={user}
        getCard={cardFor}
        stats={stats}
        statsAside={emailActions}
        totalCount={totalCount}
        onPageChange={fetchData}
        queryKey={[debouncedSearch, filters]}
        tableLoading={loading}
        toolbar={selectionBar}
        customFilters={[
          {
            key: "user_type",
            placeholder: "User Type",
            value: filters.user_type,
            options: USER_TYPE_OPTIONS,
            onChange: (v) => handleFilterChange("user_type", v),
          },
          {
            key: "platform",
            placeholder: "Platform",
            value: filters.platform,
            options: PLATFORM_OPTIONS,
            onChange: (v) => handleFilterChange("platform", v),
          },
          {
            key: "signup_state",
            placeholder: "All users",
            value: filters.signup_state,
            options: SIGNUP_STATE_OPTIONS,
            onChange: (v) => handleFilterChange("signup_state", v),
          },
          {
            key: "date",
            placeholder: "Date Added",
            value: filters.date,
            options: DATE_OPTIONS,
            onChange: (v) => handleFilterChange("date", v),
          },
        ]}
        addLabel="Add User"
        onAdd={
          canCreateUser
            ? () => {
                setHandleEditId(null);
                setIsModalOpen(true);
              }
            : undefined
        }
      />
      <UserInvite
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onInviteSuccess={fetchData}
        handleEditId={handleEditId}
        setHandleEditId={setHandleEditId}
      />
      <SendEmailModal
        open={!!emailAudience}
        audience={emailAudience}
        selectedLabel={
          allMatching
            ? `All ${totalCount.toLocaleString()} users matching the current filters`
            : `${selectedIds.size.toLocaleString()} selected user${selectedIds.size === 1 ? "" : "s"}`
        }
        onClose={() => setEmailAudience(null)}
        onSent={() => {
          setEmailAudience(null);
          stopSelecting();
        }}
      />
    </div>
  );
};

export default Users;